import React from "react";
import { Calendar, ExternalLink, FileText, User } from "react-feather";
import {
  KG_INSPECTOR_ICON_DEFAULT_SIZE,
  kgInspectorIcons,
} from "../../kg/canvas/inspector/kgInspectorIcons";
import { MAGICBI_REPORT_SOURCE } from "../../kg/canvas/inspector/reportInspectorConfig";
import type { KgInspectorTableSourceRef } from "../../kg/canvas/inspector/types";
import { KgInspectorDetailRow } from "../KgInspectorDetailRow";
import {
  DescriptionBody,
  EmptyHint,
  IconSlot,
  OverviewRoot,
  SectionBlock,
  SectionHeader,
  SectionTitle,
  SourceLinkButton,
  SourceLogo,
  SourceName,
  SourceValue,
} from "./KgInspectorReportNativeOverview.styles";
import type { KgInspectorReportNativeOverviewProps } from "./KgInspectorReportNativeOverview.types";

const DescriptionIcon = kgInspectorIcons.description;
const SourceIcon = kgInspectorIcons.source;
const sourceIconSize = KG_INSPECTOR_ICON_DEFAULT_SIZE.source;

const SourceRefValue: React.FC<{ source: KgInspectorTableSourceRef }> = ({
  source,
}) => (
  <SourceValue>
    {source.logoUrl && <SourceLogo src={source.logoUrl} alt="" aria-hidden />}
    <SourceName>{source.name}</SourceName>
    {source.externalUrl && (
      <SourceLinkButton
        href={source.externalUrl}
        target="_blank"
        rel="noreferrer"
        aria-label={`Open ${source.name} in a new tab`}
        onClick={(event) => event.stopPropagation()}
      >
        <ExternalLink size={14} />
      </SourceLinkButton>
    )}
  </SourceValue>
);

export const KgInspectorReportNativeOverview: React.FC<
  KgInspectorReportNativeOverviewProps
> = ({ overview }) => {
  const { description, source, reportType, updatedOn, updatedBy } = overview;
  const sourceRef = source ?? MAGICBI_REPORT_SOURCE;

  return (
    <OverviewRoot>
      <SectionBlock>
        <SectionHeader>
          <IconSlot>
            <DescriptionIcon size={16} />
          </IconSlot>
          <SectionTitle>Description</SectionTitle>
        </SectionHeader>
        {description ? (
          <DescriptionBody>{description}</DescriptionBody>
        ) : (
          <EmptyHint>No description available for this report yet.</EmptyHint>
        )}
      </SectionBlock>

      <KgInspectorDetailRow
        icon={<SourceIcon size={sourceIconSize} />}
        label="Source"
        value={<SourceRefValue source={sourceRef} />}
      />

      {reportType && (
        <KgInspectorDetailRow
          icon={<FileText size={16} />}
          label="Type"
          value={reportType}
        />
      )}

      {updatedOn && (
        <KgInspectorDetailRow
          icon={<Calendar size={16} />}
          label="Updated on"
          value={updatedOn}
        />
      )}

      {updatedBy && (
        <KgInspectorDetailRow
          icon={<User size={16} />}
          label="Updated by"
          value={updatedBy}
        />
      )}
    </OverviewRoot>
  );
};

export default KgInspectorReportNativeOverview;
